import React, { FC } from "react"
import { AuthForm } from "../../Common/AuthForm/AuthForm"
import { AuthFormView } from "../../../Models/Client/AuthFormView"
import { isAuthenticated } from "../../../Helpers/Functions/Selectors/auth"
import { Redirect } from "react-router"
import { routePaths } from "../../../Routes"
import { Loader, Text, Title } from "dash-styled"
import { ConsentStatement } from "../../Common/AuthForm/ConsentStatement/ConsentStatement"
import { Invitation } from "../../../Models/Client/Invitation"
import { useAppState } from "../../../Hooks/useReduxState"
import { UserType } from "../../../Models/Client/User"

export interface AuthProps {
    type: AuthFormView
    redirectTo?: string
    invitation?: Invitation
    squashSuccessMessage: boolean
    email?: string
    provider?: string
}

function isDefined<T>(some: T | undefined): some is T {
    return some !== undefined
}

export const Auth: FC<AuthProps> = ({
    type,
    redirectTo,
    invitation,
    squashSuccessMessage,
    email,
    provider
}) => {
    const authenticated = useAppState(isAuthenticated)
    const confirmUser = useAppState(state => state.auth.user)

    const existsInfo = useAppState(state => {
        if (invitation === undefined) {
            return undefined
        }
        if (invitation.type === "collaborator") {
            return state.api.authentication.ownerExists[
                invitation.collaborationId
            ]
        }

        if (invitation.type === "organization-member") {
            return state.api.authentication.orgMemberOwnerExists[
                invitation.memberId
            ]
        }

        return state.api.authentication.workerOwnerExists[invitation.workerId]
    })

    if (authenticated) {
        return <Redirect to={redirectTo ?? routePaths.dashboard()} />
    }

    if (invitation !== undefined && existsInfo?.email === undefined) {
        return <Loader />
    }

    const userChecked = isDefined(invitation) && isDefined(existsInfo?.exists)
    const typeOverwrite =
        confirmUser.type === UserType.unconfirmed
            ? AuthFormView.signup_confirm
            : userChecked && !existsInfo?.exists
            ? AuthFormView.signup
            : provider
            ? AuthFormView.login_provider
            : type

    const prefilledEmail =
        confirmUser.type === UserType.unconfirmed
            ? confirmUser.email
            : existsInfo?.email ?? email
    return (
        <div className="bg-rhino-100 w-full h-screen flex flex-col items-center py-16 px-8">
            <div style={{ width: "460px" }}>
                <Title
                    variant="heading1"
                    component="h1"
                    className={`text-center ${
                        typeOverwrite === AuthFormView.signup_confirm
                            ? "pt-6 pb-2"
                            : "py-6"
                    }`}
                    weight="semibold"
                >
                    {typeOverwrite === AuthFormView.login
                        ? "Sign in"
                        : typeOverwrite === AuthFormView.signup
                        ? "Create your account"
                        : typeOverwrite === AuthFormView.signup_confirm
                        ? "Enter verification code"
                        : typeOverwrite === AuthFormView.verify_otp
                        ? "Multi Factor Authentication"
                        : typeOverwrite === AuthFormView.forgot
                        ? "Reset your password"
                        : typeOverwrite === AuthFormView.login_provider
                        ? `Sign in with ${provider}`
                        : "Reset your password"}
                </Title>
                {typeOverwrite === AuthFormView.signup_confirm && (
                    <Text className="text-center pb-4">
                        The verification code is sent to your email
                    </Text>
                )}
                <AuthForm
                    view={typeOverwrite}
                    squashSuccessMessage={squashSuccessMessage}
                    prefilledEmail={prefilledEmail}
                    authProvider={provider}
                />
                {typeOverwrite === AuthFormView.signup && <ConsentStatement />}
            </div>
        </div>
    )
}
